import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatCurrency, formatDateOnly, formatApiError, posterSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import EventForm, { emptyForm, eventToForm } from "../components/EventForm";
import { VerifyCard, PayoutAccountCard } from "../components/OrganizerOnboarding";
import { Plus, Trash2, Users, Ticket, Wallet, CalendarClock, Calendar, Pencil, Sparkles, ShieldCheck, ShieldAlert, ScanLine, Ban } from "lucide-react";
import { Textarea } from "../components/ui/textarea";

export default function OrganizerDashboard() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState(null);
  const [modal, setModal] = useState(null); // {mode:'create'|'edit', id}
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [promote, setPromote] = useState(null);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [refunds, setRefunds] = useState([]);
  const [payoutAcct, setPayoutAcct] = useState(null);
  const nav = useNavigate();
  const suspended = user?.verification_status === "suspended";
  const verified = user?.role === "admin" || user?.verification_status === "verified";
  const locked = suspended || !verified;

  const load = async () => {
    const [{ data: evs }, { data: st }, { data: pa }, { data: rf }] = await Promise.all([api.get("/organizer/events"), api.get("/organizer/stats"), api.get("/organizer/payout-account"), api.get("/organizer/refunds")]);
    setEvents(evs); setStats(st); setPayoutAcct(pa); setRefunds(rf);
  };

  const cancelEvent = async () => {
    setSaving(true);
    try { await api.post(`/events/${cancelTarget.id}/cancel`, { reason: cancelReason }); toast.success("Event cancelled — attendees are being refunded and emailed"); setCancelTarget(null); setCancelReason(""); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  useEffect(() => { load(); if (params.get("promoted")) toast.success("Your event is now in the Spotlight for 7 days"); }, [params]);

  const openCreate = () => { setForm(emptyForm()); setModal({ mode: "create" }); };
  const openEdit = (e) => { setForm(eventToForm(e)); setModal({ mode: "edit", id: e.id }); };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, date: new Date(form.date).toISOString(), tiers: form.tiers.map(({ sold, ...t }) => t) };
    try {
      if (modal.mode === "create") { await api.post("/events", payload); toast.success("Event published"); }
      else { await api.patch(`/events/${modal.id}`, payload); toast.success("Event updated"); }
      setModal(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/events/${id}`); toast.success("Event deleted"); load();
  };

  const payForAd = async () => {
    try {
      const { data } = await api.post("/payments/ad-checkout", { event_id: promote.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const chartData = (stats?.per_event || []).map((p) => ({ name: p.event_title.length > 14 ? p.event_title.slice(0, 14) + "…" : p.event_title, Tickets: p.tickets }));
  const isLive = (e) => e.featured_until && new Date(e.featured_until) > new Date();

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
      <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">Organizer console</div>
          <h1 className="font-display text-3xl font-extrabold text-zinc-50 sm:text-4xl">Your events</h1>
          <div data-testid="verification-badge" className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest ${suspended ? "border-red-500/40 text-red-400" : verified ? "border-amber-500/40 text-amber-400" : "border-yellow-500/40 text-yellow-400"}`}>
            {suspended ? <><ShieldAlert className="h-3 w-3" /> Suspended</> : verified ? <><ShieldCheck className="h-3 w-3" /> PAN verified</> : <><ShieldAlert className="h-3 w-3" /> Verification pending</>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="door-scanner-btn" variant="outline" onClick={() => nav("/organizer/scan")} className="border-zinc-700 bg-transparent text-zinc-100 hover:border-punch-500 hover:text-punch-400"><ScanLine className="mr-2 h-4 w-4" /> Door scanner</Button>
          <Button data-testid="create-event-btn" onClick={openCreate} disabled={locked} className="bg-amber-500 font-bold uppercase tracking-widest text-zinc-950 hover:bg-amber-400"><Plus className="mr-2 h-4 w-4" /> New event</Button>
        </div>
      </div>

      {!suspended && !verified && <VerifyCard />}
      {verified && !suspended && <PayoutAccountCard account={payoutAcct} onSaved={setPayoutAcct} />}

      {suspended && <div data-testid="suspended-banner" className="mb-6 rounded-2xl border border-red-500/40 bg-red-500/5 p-4 text-sm text-red-200">Your organizer account has been suspended by Afterhours. Your events are hidden and you can't publish until it's reviewed.</div>}

      {stats && (
        <div className="mb-8 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[
            { icon: Calendar, k: "Events", v: stats.total_events, testid: "stat-events" },
            { icon: Ticket, k: "Tickets sold", v: stats.total_tickets_sold, testid: "stat-tickets" },
            { icon: Wallet, k: "Pending payout", v: formatCurrency(stats.pending_payout), testid: "stat-payout", sub: `after 1% tax · ${formatCurrency(stats.organizer_tax)} withheld` },
            { icon: CalendarClock, k: "Next payout", v: formatDateOnly(stats.next_payout_date), testid: "stat-next-payout", sub: "Every Wednesday" },
          ].map((s) => (
            <div key={s.k} data-testid={s.testid} className="rounded-2xl border border-zinc-800/80 bg-[#3A1020] p-5 transition hover:border-amber-500/30">
              <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500"><s.icon className="h-3.5 w-3.5 text-amber-500" /> {s.k}</div>
              <div className="mt-2 font-display text-2xl font-extrabold text-zinc-50">{s.v}</div>
              {s.sub && <div className="mt-1 text-[11px] text-zinc-500">{s.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {chartData.length > 0 && (
        <div className="mb-8 rounded-2xl border border-zinc-800/80 bg-[#3A1020] p-6">
          <div className="mb-4 font-mono text-[10px] uppercase tracking-widest text-amber-400">Sales by event</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="name" stroke="#71717a" fontSize={11} /><YAxis stroke="#71717a" fontSize={11} />
              <Tooltip contentStyle={{ background: "#2F0D18", border: "1px solid #27272a", borderRadius: 8, color: "#fafafa" }} cursor={{ fill: "#27272a33" }} />
              <Bar dataKey="Tickets" fill="#F2C7D2" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-zinc-800/80 bg-[#3A1020]">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400">Event</TableHead><TableHead className="text-zinc-400">Date</TableHead>
              <TableHead className="text-zinc-400">Tiers</TableHead><TableHead className="text-zinc-400">Spotlight</TableHead>
              <TableHead className="text-right text-zinc-400">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-zinc-500">No events yet. Create your first one.</TableCell></TableRow>
            ) : events.map((e) => (
              <TableRow key={e.id} data-testid={`event-row-${e.id}`} className="border-zinc-800">
                <TableCell>
                  <Link to={`/events/${e.id}`} className="flex items-center gap-3">
                    <img src={posterSrc(e.poster_url)} alt="" className="h-10 w-10 rounded-md object-cover" />
                    <div><div className="font-medium text-zinc-100">{e.title}</div><div className="font-mono text-[10px] uppercase tracking-widest text-amber-400">{e.category}{e.status === "cancelled" && <span data-testid={`cancelled-${e.id}`} className="ml-2 rounded-full bg-red-500/15 px-2 py-0.5 text-red-400">Cancelled</span>}</div></div>
                  </Link>
                </TableCell>
                <TableCell className="font-mono text-xs text-zinc-300">{formatDateOnly(e.date)}</TableCell>
                <TableCell className="text-xs text-zinc-300">{e.tiers.map((t) => <div key={t.name}>{t.name} · {formatCurrency(t.price)} · {t.quantity - t.sold}/{t.quantity} left{t.seated ? " · seated" : ""}</div>)}</TableCell>
                <TableCell>
                  {isLive(e) ? <span data-testid={`spotlight-live-${e.id}`} className="rounded-full bg-punch-500/15 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-punch-400">Live till {formatDateOnly(e.featured_until)}</span>
                    : <Button data-testid={`promote-${e.id}`} size="sm" variant="ghost" disabled={locked} onClick={() => setPromote(e)} className="h-7 text-xs text-punch-400 hover:text-punch-500"><Sparkles className="mr-1 h-3.5 w-3.5" />Promote ₹1,999</Button>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button data-testid={`edit-event-${e.id}`} variant="ghost" size="sm" disabled={locked} onClick={() => openEdit(e)} className="text-zinc-300 hover:text-amber-400"><Pencil className="mr-1 h-3.5 w-3.5" />Edit</Button>
                  <Button data-testid={`view-attendees-${e.id}`} variant="ghost" size="sm" onClick={() => nav(`/organizer/events/${e.id}/attendees`)} className="text-zinc-300 hover:text-amber-400"><Users className="mr-1 h-3.5 w-3.5" />Attendees</Button>
                  {e.status !== "cancelled" && e.tiers.some((t) => t.sold > 0)
                    ? <Button data-testid={`cancel-event-${e.id}`} variant="ghost" size="sm" onClick={() => setCancelTarget(e)} className="text-red-400 hover:text-red-300"><Ban className="mr-1 h-3.5 w-3.5" />Cancel</Button>
                    : e.status !== "cancelled" && <Button data-testid={`delete-event-${e.id}`} variant="ghost" size="sm" onClick={() => remove(e.id)} className="text-red-400 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {stats?.payouts?.length > 0 && (
        <div className="mt-8 rounded-2xl border border-zinc-800/80 bg-[#3A1020] p-6">
          <div className="mb-3 font-mono text-[10px] uppercase tracking-widest text-amber-400">Payout history</div>
          <div className="space-y-2 text-sm">
            {stats.payouts.map((p) => (
              <div key={p.id} data-testid={`payout-${p.id}`} className="flex items-center justify-between rounded-lg border border-zinc-800 p-3">
                <div><div className="text-zinc-100">{formatDateOnly(p.created_at)} · {p.tickets} tickets</div><div className="text-xs text-zinc-500">Gross {formatCurrency(p.gross)} − 1% tax {formatCurrency(p.organizer_tax)}</div></div>
                <div className="text-right"><div className="font-mono font-bold text-amber-400">{formatCurrency(p.amount)}</div><div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{p.status}</div></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!modal} onOpenChange={(o) => !o && setModal(null)}>
        <DialogContent data-testid="event-modal" className="max-h-[90vh] overflow-y-auto border-zinc-800 bg-[#33101C] text-zinc-100 sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-display">{modal?.mode === "edit" ? "Edit event" : "Launch a new event"}</DialogTitle></DialogHeader>
          {modal && <EventForm form={form} setForm={setForm} onSubmit={submit} onCancel={() => setModal(null)} saving={saving} submitLabel={modal.mode === "edit" ? "Save changes" : "Publish event"} />}
        </DialogContent>
      </Dialog>

      {refunds.length > 0 && (
        <div className="mt-8 rounded-2xl border border-red-500/30 bg-[#3A1020] p-6">
          <div className="mb-3 font-mono text-[10px] uppercase tracking-widest text-red-400">Refunds issued (cancelled events)</div>
          <div className="space-y-2 text-sm">
            {refunds.map((r) => (
              <div key={r.id} data-testid={`refund-${r.id}`} className="flex items-center justify-between rounded-lg border border-zinc-800 p-3">
                <div><div className="text-zinc-100">{r.user_name} · {r.event_title}</div><div className="text-xs text-zinc-500">{r.booking_code}{r.organizer_clawback > 0 ? ` · ${formatCurrency(r.organizer_clawback)} recovered from your next payout` : ""}</div></div>
                <div className="text-right"><div className="font-mono font-bold text-red-300">−{formatCurrency(r.amount)}</div><div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{r.status}</div></div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!cancelTarget} onOpenChange={(o) => !o && setCancelTarget(null)}>
        <DialogContent data-testid="cancel-modal" className="border-zinc-800 bg-[#33101C] text-zinc-100 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-red-300"><Ban className="h-5 w-5" /> Cancel "{cancelTarget?.title}"?</DialogTitle>
            <DialogDescription className="text-zinc-400">Every ticket holder gets a full refund (ticket + booking fee) and a cancellation email. Amounts already paid out to you are recovered from your next Wednesday payout. This can't be undone.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="cancel-reason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Reason attendees will see (e.g. venue unavailable)" className="border-zinc-800 bg-[#2F0D18]" rows={3} />
          <Button data-testid="cancel-confirm-btn" onClick={cancelEvent} disabled={saving || cancelReason.trim().length < 3} className="w-full bg-red-500 font-bold text-white hover:bg-red-600">{saving ? "Cancelling…" : "Cancel event & refund everyone"}</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!promote} onOpenChange={(o) => !o && setPromote(null)}>
        <DialogContent data-testid="promote-modal" className="border-zinc-800 bg-[#33101C] text-zinc-100 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Sparkles className="h-5 w-5 text-punch-400" /> Put it in the Spotlight</DialogTitle>
            <DialogDescription className="text-zinc-400">Your event rotates in the big hero slot on the Afterhours homepage for 7 days. Multiple organizers share the slider.</DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-zinc-800 bg-[#2F0D18] p-4 text-sm">
            <div className="flex justify-between"><span className="text-zinc-300">{promote?.title}</span><span className="font-mono text-zinc-400">7 days</span></div>
            <div className="mt-2 flex justify-between border-t border-zinc-800 pt-2"><span className="font-semibold">Total</span><span className="font-mono font-bold text-amber-400">{formatCurrency(stats?.ad_price || 1999)}</span></div>
          </div>
          <Button data-testid="promote-pay-btn" onClick={payForAd} className="w-full bg-punch-500 font-bold text-white hover:bg-punch-600">Pay with Stripe</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
